/**
 * liteQuestions.js — single source of truth for the Lite wizard:
 * step definitions, the 26 risk questions (keys MUST match
 * backend/mvaSchema.js createEmptyLiteAnswers), per-step validation,
 * and assembly of the MVAInput payload for POST /api/lite/valuations.
 *
 * Rating scale is the workbook appraiser scale 0..4 where HIGHER = STRONGER
 * business (engine: riskAdjustment = 0.65 - weighted; Z = base x (1 - adj)).
 */

export const RATING_LABELS = {
  0: 'Major weakness',
  1: 'Below average',
  2: 'Average',
  3: 'Strong',
  4: 'Excellent',
};

export const INDUSTRY_OPTIONS = [
  { value: 'services', label: 'Services' },
  { value: 'professionalServices', label: 'Professional Services' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'retail', label: 'Retail' },
  { value: 'trades', label: 'Trades & Construction' },
  { value: 'tech', label: 'Technology & Software' },
  { value: 'finance', label: 'Finance & Insurance' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'hospitality', label: 'Hospitality & Food Service' },
  { value: 'transportation', label: 'Transportation & Logistics' },
  { value: 'other', label: 'Other' },
];

// 14 VAC-derived + 5 Company Risk + 7 Industry Risk. Order matters: steps
// slice this list. help text keeps owners oriented; 0..4 anchors above.
export const RISK_QUESTIONS = [
  // --- VAC-derived (14) — step 4 ---
  { key: 'ownerDependency', label: 'Owner independence', help: 'How well does the business operate without you day-to-day? 0 = cannot run without the owner, 4 = runs entirely without the owner.' },
  { key: 'customerConcentration', label: 'Customer diversification', help: 'How spread out is your revenue? 0 = one customer dominates, 4 = no customer is more than a few percent.' },
  { key: 'revenueGrowthTrend', label: 'Revenue trend', help: 'Direction of revenue over the last 3 years. 0 = declining sharply, 2 = flat, 4 = growing strongly.' },
  { key: 'marginTrend', label: 'Margin trend', help: 'Direction of profit margins over the last 3 years. 0 = shrinking fast, 4 = expanding.' },
  { key: 'keyPersonRisk', label: 'Key-person resilience', help: 'If your most important employee (other than you) left tomorrow: 0 = severe disruption, 4 = barely noticeable.' },
  { key: 'processDocumentation', label: 'Process documentation', help: 'How well are core processes written down? 0 = all in people’s heads, 4 = fully documented and followed.' },
  { key: 'competitivePosition', label: 'Competitive position', help: 'How well you stand out from direct competitors. 0 = competing mainly on price with nothing to set you apart, 4 = clear market leader locally.' },
  { key: 'recurringRevenuePercent', label: 'Recurring revenue', help: 'Share of revenue that is contracted or repeats reliably. 0 = none, 4 = mostly recurring.' },
  { key: 'managementDepth', label: 'Management depth', help: 'Strength of the team below the owner. 0 = no managers, 4 = complete capable leadership team.' },
  { key: 'successionPlan', label: 'Succession readiness', help: 'Is there a plan for who runs things next? 0 = none, 4 = documented and rehearsed.' },
  { key: 'contractQuality', label: 'Contract quality', help: 'Are customer relationships under written, transferable contracts? 0 = handshake deals, 4 = strong transferable contracts.' },
  { key: 'systemsTechMaturity', label: 'Systems & technology', help: 'Quality of your operating systems and software. 0 = outdated/manual, 4 = modern and well-integrated.' },
  { key: 'regulatoryExposure', label: 'Regulatory standing', help: 'Exposure to licences, audits and compliance risk. 0 = at-risk or non-compliant, 4 = fully compliant with low exposure.' },
  { key: 'marketShareTrend', label: 'Market share trend', help: 'Your slice of the local market over time. 0 = shrinking, 4 = growing.' },
  // --- Company Risk (5) — step 5 ---
  { key: 'arAgingQuality', label: 'Receivables quality', help: 'How quickly do customers pay? 0 = chronically late/heavy bad debt, 4 = consistently on time.' },
  { key: 'cashFlowPredictability', label: 'Cash-flow predictability', help: 'Month-to-month cash stability. 0 = volatile and hard to forecast, 4 = very steady.' },
  { key: 'ipPortfolio', label: 'Intellectual property', help: 'Trademarks, patents, proprietary methods or brands. 0 = none, 4 = strong protected portfolio.' },
  { key: 'litigationHistory', label: 'Litigation history', help: 'Legal disputes in the last 5 years. 0 = ongoing/serious litigation, 4 = clean record.' },
  { key: 'boardGovernance', label: 'Financial reporting', help: 'How often the numbers are reviewed, and how. 0 = once a year with the accountant, 4 = live financial dashboard.' },
  // --- Industry Risk (7) — step 5 (these DO score today: Industry Risk tier) ---
  { key: 'industryRegulatoryEnvironment', label: 'Industry: regulatory climate', help: 'How friendly is regulation to your industry? 0 = hostile/tightening, 4 = stable and supportive.' },
  { key: 'industryEnvironmentalExposure', label: 'Industry: environmental exposure', help: 'Exposure to environmental liabilities or climate impact. 0 = high exposure, 4 = minimal.' },
  { key: 'industryBusinessModelRobustness', label: 'Industry: business-model durability', help: 'Is the way this industry makes money under threat (disruption, substitution)? 0 = being disrupted, 4 = very durable.' },
  { key: 'industryGrowthTrend', label: 'Industry: growth outlook', help: 'Industry trajectory over the next 5 years. 0 = declining, 2 = flat, 4 = growing fast.' },
  { key: 'industryMarketSentiment', label: 'Industry: buyer sentiment', help: 'How eager are buyers/investors for businesses like yours? 0 = avoided, 4 = highly sought after.' },
  { key: 'industryDemandStability', label: 'Industry: demand stability', help: 'How steady is customer demand through economic cycles? 0 = highly cyclical, 4 = recession-resistant.' },
  { key: 'industryLifecycleStage', label: 'Industry: lifecycle stage', help: 'Where is the industry in its lifecycle? 0 = late decline, 2 = mature, 4 = growing.' },
];

const VAC_KEYS = RISK_QUESTIONS.slice(0, 14).map((q) => q.key);
const COMPANY_AND_INDUSTRY_KEYS = RISK_QUESTIONS.slice(14).map((q) => q.key);

export const WIZARD_STEPS = [
  { id: 'company', title: 'Your business', questionKeys: [] },
  { id: 'earnings', title: 'Earnings history', questionKeys: [] },
  { id: 'balanceSheet', title: 'Balance sheet', questionKeys: [] },
  { id: 'riskCore', title: 'Business strength (1 of 2)', questionKeys: VAC_KEYS },
  { id: 'riskMarket', title: 'Business strength (2 of 2)', questionKeys: COMPANY_AND_INDUSTRY_KEYS },
  { id: 'review', title: 'Review & calculate', questionKeys: [] },
];

export function createEmptyFormData() {
  return {
    // step 1
    companyName: '',
    industry: '',
    foundingYear: '',
    employeeCount: '',
    location: '',
    canadianCompany: false,
    // step 2
    lastFiscalYear: '',
    ebitdaAnchor: '',
    ebitdaMinus1: '',
    ebitdaMinus2: '',
    ebitdaMinus3: '',
    ebitdaMinus4: '',
    sdeAnchor: '',
    sdeMinus1: '',
    sdeMinus2: '',
    sdeMinus3: '',
    sdeMinus4: '',
    // step 3
    totalAssets: '',
    totalLiabilities: '',
    inventory: '',
    // steps 4-5
    riskAnswers: RISK_QUESTIONS.reduce((acc, q) => ({ ...acc, [q.key]: null }), {}),
    // step 6 (advanced, optional)
    ruleOfThumbMultiple: '',
    addInventory: false,
  };
}

function toNumber(value) {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function isBlank(value) {
  return value === '' || value === null || value === undefined;
}

export function validateStep(stepIndex, fd) {
  const errors = {};
  if (stepIndex === 0) {
    if (!INDUSTRY_OPTIONS.some((o) => o.value === fd.industry)) {
      errors.industry = 'Select your industry';
    }
  }
  if (stepIndex === 1) {
    if (isBlank(fd.lastFiscalYear) || !/^\d{4}$/.test(String(fd.lastFiscalYear))) {
      errors.lastFiscalYear = 'Enter the last completed fiscal year (e.g. 2025)';
    }
    if (toNumber(fd.ebitdaAnchor) === null) errors.ebitdaAnchor = "Enter last year's operating profit (EBITDA)";
    if (toNumber(fd.sdeAnchor) === null) errors.sdeAnchor = "Enter last year's total owner benefit (SDE)";
    ['ebitdaMinus1', 'ebitdaMinus2', 'ebitdaMinus3', 'ebitdaMinus4',
     'sdeMinus1', 'sdeMinus2', 'sdeMinus3', 'sdeMinus4'].forEach((k) => {
      if (!isBlank(fd[k]) && toNumber(fd[k]) === null) errors[k] = 'Must be a number';
    });
  }
  if (stepIndex === 2) {
    const assets = toNumber(fd.totalAssets);
    const liabs = toNumber(fd.totalLiabilities);
    if (assets === null || assets < 0) errors.totalAssets = 'Enter total assets (0 or more)';
    if (liabs === null || liabs < 0) errors.totalLiabilities = 'Enter total liabilities (0 or more)';
    if (!isBlank(fd.inventory) && (toNumber(fd.inventory) === null || toNumber(fd.inventory) < 0)) {
      errors.inventory = 'Inventory must be a non-negative number';
    }
  }
  if (stepIndex === 3 || stepIndex === 4) {
    WIZARD_STEPS[stepIndex].questionKeys.forEach((key) => {
      const v = fd.riskAnswers[key];
      if (!(Number.isInteger(v) && v >= 0 && v <= 4)) errors[key] = 'Pick a rating';
    });
  }
  if (stepIndex === 5) {
    if (!isBlank(fd.ruleOfThumbMultiple)) {
      const m = toNumber(fd.ruleOfThumbMultiple);
      if (m === null || m <= 0 || m > 15) errors.ruleOfThumbMultiple = 'Multiple must be between 0 and 15';
    }
  }
  return errors;
}

export function buildMVAInput(fd) {
  return {
    meta: { variant: 'lite', schemaVersion: 1, submittedAt: null, userId: null },
    company: {
      name: fd.companyName || null,
      industry: fd.industry || null,
      subIndustry: null,
      foundingYear: toNumber(fd.foundingYear),
      employeeCount: toNumber(fd.employeeCount),
      location: fd.location || null,
    },
    financials: {
      lastFiscalYear: toNumber(fd.lastFiscalYear),
      historicalEBITDA: {
        yearMinus4: toNumber(fd.ebitdaMinus4),
        yearMinus3: toNumber(fd.ebitdaMinus3),
        yearMinus2: toNumber(fd.ebitdaMinus2),
        yearMinus1: toNumber(fd.ebitdaMinus1),
        yearAnchor: toNumber(fd.ebitdaAnchor),
      },
      historicalSDE: {
        yearMinus4: toNumber(fd.sdeMinus4),
        yearMinus3: toNumber(fd.sdeMinus3),
        yearMinus2: toNumber(fd.sdeMinus2),
        yearMinus1: toNumber(fd.sdeMinus1),
        yearAnchor: toNumber(fd.sdeAnchor),
      },
      projectedEBITDA: null,
      projectedSDE: null,
      dealStructure: fd.canadianCompany ? 'YES' : 'NO', // workbook H34 "Canadian Company"
      balanceSheet: {
        summary: {
          totalAssets: toNumber(fd.totalAssets),
          totalLiabilities: toNumber(fd.totalLiabilities),
          inventory: toNumber(fd.inventory),
        },
        lineItems: null,
      },
      recastAdjustments: null,
    },
    weights: null, // engine default: Industry Rule of Thumb = 1.0
    ownerOperated: {
      lenderInterestRate: 0.08,
      loanTermYears: 10,
      buyerDownPayment: 0.30,
      capexReinvestPct: 0.15,
      targetDividendPct: 0.15,
      targetROI: 0.30,
      capexReservePct: 0.10,
      marketSalary: 100000,
    },
    ruleOfThumb: {
      multiple: toNumber(fd.ruleOfThumbMultiple) ?? 3.0,
      addInventory: fd.addInventory ? 'YES' : 'NO',
    },
    risk: {
      mode: 'lite',
      liteAnswers: { ...fd.riskAnswers },
      heavyAnswers: null,
      sizeOverride: null,
      industryOverride: null,
      adjustmentOverride: null,
    },
    referenceDataSnapshot: {
      brgMultiplesVersion: null,
      industryBenchmarksVersion: null,
      effectiveDate: null,
    },
  };
}
